import 'rxjs/add/observable/of';
import 'rxjs/add/operator/debounceTime';
import {Component, Input, NgZone, OnDestroy, OnInit} from '@angular/core';
import {ParkProvider} from '@providers/park.provider';
import {FileProvider} from '@providers/file.provider';
import {Area} from '@core/area';
import {World} from '@core/world';
import {Kingdom} from '@core/kingdom';
import {LandEvent} from '@core/broadcaster/LandEvent';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {OpenLandModalEvent} from '@events/OpenLandModalEvent';
import {ValidatorsService} from '@core/validators/validators.service';
import {ThingsUtil} from '../../../util/things';
import {ParkService} from "@core/park/park.service";
import {KngActivityIndicatorService} from "@layout/kingswell/services/kngActivityIndicator.service";
import {Subscription} from "rxjs/Subscription";
import { KngDrawerService } from '@layout/kingswell/kingswell';
import { AreasImageSelector } from './imageSelector.component';
import { IMAGE_BASE_PATH } from '../../../constants/images.constants';
import { SEPARATE_TRASH_TEXT } from '../../../constants/things.constants';
import { RestroomAlertComponent } from '../../things/restroom-alert/restroom-alert.component';
import { OpenGroupEvent } from '@events/OpenGroupEvent';
import { ActivatedRoute, NavigationExtras, Router } from '@angular/router';
// import { LandModalEvent } from '@events/LandModalEvent';

interface MyFormGroup {
  [key: string]: any;
}

@Component({
  selector: 'areas-form',
  templateUrl: './form.component.html',
  styleUrls: ['./form.component.less'],
})
export class AreasFormComponent implements OnInit, OnDestroy {

  @Input()
  public area: Area = new Area();
  @Input()
  public onSubmit: Function;
  @Input()
  public title: string;
  @Input()
  public key: string;

  public open: boolean = false;
  public worlds: any[];
  public kingdoms: any[];
  public selectedKingdom: Kingdom;
  public selectedWorld: World;

  public form: FormGroup;
  public imageSrc: string;

  private file: File;
  private isImageError: boolean;

  public baseImagePath = IMAGE_BASE_PATH + 'areas/';

  public inactiveTextForm = SEPARATE_TRASH_TEXT.separateTrashAndRecycleInactive;

  public activeTextForm = SEPARATE_TRASH_TEXT.separateTrashAndRecycleActive;

  public cansTaskProximityOptions: Array<any> = [];
  public custodiansTaskProximityOptions: Array<any> = [];

  public containsActiveThings: boolean = false;

  public showHeaderBottomLine:boolean = false;

  public openingTime: Number = 0;
  public closingTime: Number = 0;
  public currentTime: Number = new Date().getTime();

  private openLandModalEventSubscription : Subscription;

  constructor(private zone: NgZone, private kngActivityIndicator: KngActivityIndicatorService,
              private park: ParkProvider, private parkService: ParkService,
              private LandEvent: LandEvent, private openLandModalEvent: OpenLandModalEvent,
              private openGroupEvent: OpenGroupEvent,
              // private LandModalEvent: LandModalEvent,
              private fb: FormBuilder, private validator: ValidatorsService,
              private fileProvider: FileProvider, private thingsUtil: ThingsUtil,
              private kngDrawer: KngDrawerService, private router: Router) {
    this.form = this.fb.group(this.getFormMap({}) );
  }

  getFormMap(area: any) : MyFormGroup {
    area = area || {};
    let taskConfig = area.taskConfig || {};
    let bounds = area.bounds || {};
    let cansPerTask = taskConfig.maxThingsPerTask || null;
    let cansPerTaskCloseout = taskConfig.maxThingsPerTaskCloseout || null;
    return {
      'areaId': [area.areaId],
      'world': [area.world, Validators.required],
      'kingdom': [area.kingdom, Validators.required],
      'name': [area.name, Validators.required],
      'active': [!! area.active, Validators.required],
      'separateTrashAndRecycle': [!! taskConfig.separateTrashAndRecycle, Validators.required],
      'cansPerTask': [cansPerTask, [Validators.required, Validators.min(0), this.validator.isInteger]],
      'cansPerTaskCloseout': [cansPerTaskCloseout, [Validators.required, Validators.min(0), this.validator.isInteger]],
      'canThreshold': [taskConfig.highThreshold, [Validators.required, Validators.min(0), Validators.max(100), this.validator.isInteger]],
      'canCloseoutThreshold': [taskConfig.lowThreshold, [Validators.required, Validators.min(0), Validators.max(100), this.validator.isInteger]],
      'canMinGroupThreshold': [taskConfig.groupThreshold, [Validators.required, Validators.min(0), Validators.max(100), this.validator.isInteger]],
      'canMinGroupThresholdCloseout': [taskConfig.groupThresholdCloseout, [Validators.required, Validators.min(0), Validators.max(100), this.validator.isInteger]],
      'canMinGroupThresholdSecondary': [taskConfig.maxDistanceForThingSecondary, [Validators.min(0), this.validator.isInteger]],
      'canMinGroupThresholdCloseoutSecondary': [taskConfig.maxDistanceForThingCloseoutSecondary, [Validators.min(0), this.validator.isInteger]],
      'showGroupCansByTaskProximity': !! (taskConfig && taskConfig.maxDistanceForThing),
      'showGroupCansByTaskProximityCloseout': !! (taskConfig && taskConfig.maxDistanceForThingCloseout),
      'showAssignCustodianByTaskProximity': !! (taskConfig && taskConfig.maxDistanceForTaskAssignment),
      'restroomThreshold': [taskConfig.minutesToCreateNextTask, [Validators.required, Validators.min(0), this.validator.isInteger]],
      'cansTaskProximity': [taskConfig.maxDistanceForThing, [Validators.min(0), this.validator.isInteger]],
      'cansTaskProximityCloseout': [taskConfig.maxDistanceForThingCloseout, [Validators.min(0), this.validator.isInteger]],
      'custodiansTaskProximity': [taskConfig.maxDistanceForTaskAssignment, []],
      'closeout': [taskConfig.closeoutReminderMinutes, [Validators.required, this.validator.isInteger]],
      'taskConfig': [taskConfig],
      'lat1': [bounds.lat1, Validators.required],
      'lon1': [bounds.lon1, Validators.required],
      'lat2': [bounds.lat2, Validators.required],
      'lon2': [bounds.lon2, Validators.required],
      'centerLat': [area.lat, Validators.required],
      'centerLon': [area.lon, Validators.required],
      'lowBatteryThreshold': [taskConfig.lowBatteryThreshold, Validators.required],
      'minutesToTaskNotCompletedLeaderNotification': [taskConfig.minutesToTaskNotCompletedLeaderNotification, [Validators.required, Validators.min(0), this.validator.isInteger]],
      'minutesToTaskNotCompletedPropNotification': [taskConfig.minutesToTaskNotCompletedPropNotification, [Validators.required, Validators.min(0), this.validator.isInteger]],
      'minutesToTaskNotAcceptedNotification': [taskConfig.minutesToTaskNotAcceptedNotification, [Validators.required, Validators.min(0), this.validator.isInteger]],
      'minutesToTaskNotCompletedNotification': [taskConfig.minutesToTaskNotCompletedNotification, [Validators.required, Validators.min(0), this.validator.isInteger]],
      'minutesToTimeTaskNotCompletedNotification': [taskConfig.minutesToTimeTaskNotCompletedNotification, [Validators.required, Validators.min(0), this.validator.isInteger]],
      'minutesToSRRTaskNotCompletedNotification': [taskConfig.minutesToSRRTaskNotCompletedNotification, [Validators.required, Validators.min(0), this.validator.isInteger]],
      'minutesToMRRTaskNotCompletedNotification': [taskConfig.minutesToMRRTaskNotCompletedNotification, [Validators.required, Validators.min(0), this.validator.isInteger]],
      'minutesToLRRTaskNotCompletedNotification': [taskConfig.minutesToLRRTaskNotCompletedNotification, [Validators.required, Validators.min(0), this.validator.isInteger]],
      'minutesBetweenLowBatterySensor': [taskConfig.minutesBetweenLowBatterySensor, Validators.required],
      'minutesToUnresponsiveSensor': [taskConfig.minutesToUnresponsiveSensor, Validators.required],
      'sensorNotificationCheckRate': [taskConfig.sensorNotificationCheckRate, Validators.required],
      'unresponsiveSensorCombineThreshold': [taskConfig.unresponsiveSensorCombineThreshold, Validators.required]
    } as MyFormGroup;
  }

  saveFileIfNecessary(area: Area, file: File): Promise<any> {
    if (this.file) {
      // we can upload the file asynchronously
      area.imageSrc = "images/areas/" + area.areaId;
      return this.fileProvider.getSignedPutURL(this.file, area.imageSrc)
        .then((signedURLResponse: any) => {
          // now upload the file
          let url = signedURLResponse.url;
          return this.fileProvider.uploadFile(url, this.file);
        })
    } else {
      return Promise.resolve();
    }
  }

  createAreaId(world, kingdom, name): string {
    if (!world || !world.worldId)
      throw Error("World does not exist");

    if (!kingdom || !kingdom.kingdomId)
      throw Error("Kingdom  does not exist");

    if (!name)
      throw Error("name  does not exist");
    return world.worldId + '-' + kingdom.kingdomId + '-' + name.replace(/\s+/g, '').toLowerCase();
  }

  intOrNull(val) {
    let newVal = parseInt(val);
    return isNaN(newVal) ? null : newVal;
  }

  floatOrNull(val) {
    let newVal = parseFloat(val);
    return isNaN(newVal) ? null : newVal;
  }

  submitForm(value: any):void {
    this.kngActivityIndicator.show();
    let area = new Area();

    area.areaId = value.areaId;
    if (! this.isEdit()) {
      area.areaId = this.createAreaId(value.world, value.kingdom, value.name);
    }
    area.name = value.name;
    area.lat = this.floatOrNull(value.centerLat);
    area.lon = this.floatOrNull(value.centerLon);
    area.taskConfig = Object.assign({}, value.taskConfig, {
      minutesToCreateNextTask: this.intOrNull(value.restroomThreshold),
      maxThingsPerTask: this.intOrNull(value.cansPerTask),
      maxThingsPerTaskCloseout: this.intOrNull(value.cansPerTaskCloseout),
      highThreshold: this.intOrNull(value.canThreshold),
      lowThreshold: this.intOrNull(value.canCloseoutThreshold),
      groupThreshold: this.intOrNull(value.canMinGroupThreshold),
      groupThresholdCloseout: this.intOrNull(value.canMinGroupThresholdCloseout),
      maxDistanceForThing: this.intOrNull(value.cansTaskProximity),
      maxDistanceForThingCloseout: this.intOrNull(value.cansTaskProximityCloseout),
      maxDistanceForThingSecondary: this.intOrNull(value.canMinGroupThresholdSecondary),
      maxDistanceForThingCloseoutSecondary: this.intOrNull(value.canMinGroupThresholdCloseoutSecondary),
      maxDistanceForTaskAssignment: this.intOrNull(value.custodiansTaskProximity),
      closeoutReminderMinutes: this.intOrNull(value.closeout),
      lowBatteryThreshold: this.intOrNull(value.lowBatteryThreshold),
      minutesToTaskNotCompletedLeaderNotification: this.intOrNull(value.minutesToTaskNotCompletedLeaderNotification),
      minutesToTaskNotCompletedPropNotification: this.intOrNull(value.minutesToTaskNotCompletedPropNotification),
      minutesToTaskNotAcceptedNotification: this.intOrNull(value.minutesToTaskNotAcceptedNotification),
      minutesToTaskNotCompletedNotification: this.intOrNull(value.minutesToTaskNotCompletedNotification),
      minutesToTimeTaskNotCompletedNotification: this.intOrNull(value.minutesToTimeTaskNotCompletedNotification),
      minutesToSRRTaskNotCompletedNotification: this.intOrNull(value.minutesToSRRTaskNotCompletedNotification),
      minutesToMRRTaskNotCompletedNotification: this.intOrNull(value.minutesToMRRTaskNotCompletedNotification),
      minutesToLRRTaskNotCompletedNotification: this.intOrNull(value.minutesToLRRTaskNotCompletedNotification),
      minutesToUnresponsiveSensor: this.intOrNull(value.minutesToUnresponsiveSensor),
      sensorNotificationCheckRate: this.intOrNull(value.sensorNotificationCheckRate),
      unresponsiveSensorCombineThreshold: this.intOrNull(value.unresponsiveSensorCombineThreshold),
      minutesBetweenLowBatterySensor: this.intOrNull(value.minutesBetweenLowBatterySensor),
      separateTrashAndRecycle: value.separateTrashAndRecycle
    });

    area.bounds = {
      lat1: this.floatOrNull(value.lat1),
      lon1: this.floatOrNull(value.lon1),
      lat2: this.floatOrNull(value.lat2),
      lon2: this.floatOrNull(value.lon2)
    };
    area.imageSrc = this.area.imageSrc;

    if (this.containsActiveThings) {
      // If contain active things, then always set to true
      area.active = true;
    } else {
      area.active = value.active;
    }
    // TODO: Update once we support DL
    area.timezone = "US/Eastern";
    
    if (this.onSubmit) {
      this.open = false;
      this.saveFileIfNecessary(area, this.file)
        .then(() => this.onSubmit(area, area.areaId))
        .catch(err => {
          console.error(err);
          this.kngActivityIndicator.close();
        });
    }
  }

  ngOnInit() {
    this.openLandModalEvent.on().subscribe(data => {
      const key: string = data.key;
      const eventArea: Area = data.land;
      if (key === this.key) {
        this.kngActivityIndicator.show();
        this.open = true;
        console.log("opening form modal for areas")

        let promises = [];

        if (eventArea.areaId) {
          promises.push(this.park.getArea(eventArea.areaId).toPromise());
        }

        promises.push(this.park.getAreaDefaults());

        Promise.all(promises)
          .then(results => {
            let area: Area = new Area();

            if (results.length > 1) {
              area = results[0] as Area;
            }

            let areaDefaults = results[results.length-1];
            let taskConfigDefaults = new Area().taskConfig;
            this.area = area;
            if(this.isEdit()) {
              this.openingTime = this.openingDateTime(area.openingTime.replace(' ', ''));
              this.closingTime = this.closingDateTime(area.closingTime.replace(' ', ''));
            }

            this.open = true;
            let w = this.getWorld(area.areaId);
            if (w) {
              this.selectedWorld = this.worlds.find(world => world.worldId === w);
            }
            let k = this.getKingdom(area.areaId);
            if (k) {
              this.kingdoms = this.selectedWorld.kingdoms;
              this.selectedKingdom = this.kingdoms.find(kingdom => kingdom.kingdomId === k);
            }

            this.form = this.fb.group(this.getFormMap({
              areaId: area.areaId,
              active: area.active !== false,
              world: this.selectedWorld,
              kingdom: this.selectedKingdom,
              lat: area.lat,
              lon: area.lon,
              name: area.name,
              taskConfig: Object.assign({}, taskConfigDefaults, areaDefaults, area.taskConfig),
              bounds: area.bounds
            }));

            // Add debounce Async validator on the name to check for duplicates
            let nameFormControl = this.form.controls['name'];
            if (! this.isEdit()) {
              nameFormControl.valueChanges
                .debounceTime(750)
                .subscribe((x) => {
                  let areaId = this.createAreaId(this.form.controls['world'].value,
                    this.form.controls['kingdom'].value,
                    this.form.controls['name'].value);

                  this.park.checkAreaExists(areaId)
                    .subscribe((res) => {
                      if (res)
                        nameFormControl.setErrors({"nameAlreadyExists": res});
                      else
                        nameFormControl.setErrors(null);
                    })
                });
            }

            this.isImageError = false;
            if (area.imageSrc) {
              this.imageSrc = area.imageSrc;
            } else {
              this.imageSrc = undefined;
            }

            this.cansTaskProximityOptions = this.getTaskProximityOptions();
            this.custodiansTaskProximityOptions = this.getTaskProximityOptions();

            // Ignore thing active check if new area
            if (this.area.areaId) {
              // Check if there are active things
              this.park.getThings(this.area.areaId, undefined, true)
                .then(things => {
                  this.containsActiveThings = things.filter(thing => this.thingsUtil.isCan(thing) ? !thing.deactivated : !thing.closed).length > 0;
                  
                  if (this.containsActiveThings) {
                    this.form.controls['active'].setValue(true);
                  }
                });
            }
            this.kngActivityIndicator.close();
          });
      }
    });

    this.parkService.getWorldsKingdomsLands()
      .then(park => {
        if( park.worlds.length > 0 ){
          this.worlds = park.worlds;
          this.selectedWorld = this.worlds[0];
          this.kingdoms = this.selectedWorld.kingdoms;
          this.selectedKingdom = this.kingdoms[0];
        }
      });
  }

  ngOnDestroy() {
    if (this.openLandModalEventSubscription)
      this.openLandModalEventSubscription.unsubscribe();
  }

  getTaskProximityOptions() {
    return [
      {
        label: 'Any distance',
        value: false
      },
      {
        label: 'Within specified distance (meters)',
        value: true
      }
    ];
  }

  updateKingdoms() {
    this.kingdoms = this.selectedWorld.kingdoms;
    this.selectedKingdom = this.kingdoms[0];
  }

  getWorld(areaId) {
    const segments = areaId.split('-');

    return segments && segments.length && segments[0];
  }

  getKingdom(areaId) {
    const segments = areaId.split('-');

    return segments && segments.length > 1 && segments[1];
  }

  handleFileInputChange(e) {
    let file = e.dataTransfer ? e.dataTransfer.files[0] : e.target.files[0];

    this.isImageError = false;

    let pattern = /image\/(jpeg|jpg|png)/;
    let reader = new FileReader();

    let _formController = this;

    console.log(file.type);
    if (!file.type.match(pattern)) {
      this.isImageError = true;
      return;
    }

    // This sections sets the imageSrc as a local data image URL
    reader.onload = function () {
      _formController.imageSrc = reader.result;
      _formController.file = file;
    };
    reader.readAsDataURL(file);
  }

  close() {
    this.open = false;
  }

  isOpen() {
    return this.open === true;
  }

  closePanel() {
    this.close();
  }

  cancel() {
    this.close();
  }

  onWorldChange() {
    this.selectedWorld = this.form.controls['world'].value;
    this.updateKingdoms();
  }

  isEdit() {
    return this.key === 'edit';
  }

  openingDateTime(time){
    // console.log("Input time: " + time)
    const PM = time.match('PM') ? true : false;
    const openingTime = time.split(':');
    const minutes = openingTime[1].substring(0,2); // [00]
    const openingDate = new Date();
    let hour;

    // console.log("Is it PM Time: " + PM)
    // console.log("Opening Time Contents " + openingTime[0] + "       " + openingTime[1])

    if (PM) {
        hour = 12 + parseInt(openingTime[0],10);
        openingDate.setHours(hour, minutes);
    } else {
        hour = openingTime[0]; // 09 
        openingDate.setHours(hour, minutes);  
    }

    // console.log("Opening Info: " + openingDate);
    return openingDate.getTime(); 
  }

  closingDateTime(time){
    const PM = time.match('PM') ? true: false
    const closingTime = time.split(':')
    const minutes = closingTime[1].substring(0,2); // remove 'PM' or 'AM'
    const closingDate = new Date();
    let hour;

    // console.log("Is it PM Time: " + PM)
    // console.log("Closing Time Contents " + closingTime[0] + "       " + closingTime[1])

    if(PM) {
      hour = 12 + parseInt(closingTime[0], 10)
      closingDate.setHours(hour, minutes)
    } else {
      hour = closingTime[0];
      const month = closingDate.getMonth(); //ex: Jan = 0
      const date = closingDate.getDate();
      switch(month){
        case 0:
        case 2:
        case 4:
        case 6:
        case 7:
        case 9:
        case 11:
          if(date === 31){
            if(month == 11){ //dec 31st
              closingDate.setMonth(0);
              closingDate.setDate(1);
              closingDate.setFullYear(closingDate.getFullYear()+1);
            } else { // oct 31
              closingDate.setMonth(month+1); //9+1= 10 = nov.
              closingDate.setDate(1);
            } 
          } else {
            closingDate.setDate(date+1);
          }
          closingDate.setHours(hour, minutes);
          break;
        case 3:
        case 5:
        case 8:
        case 10:
          if(date === 30){ //nov 30
            closingDate.setMonth(month+1); //
            closingDate.setDate(1);
          } else {
            closingDate.setDate(date+1);
          }
        closingDate.setHours(hour, minutes);
        break;
        case 1:
          const leapYear = (closingDate.getFullYear() % 100 ===0) ? (closingDate.getFullYear() % 400 ===0) : (closingDate.getFullYear() % 4 ===0);
          if(leapYear){
            if(date === 29){
              closingDate.setMonth(month+1);
              closingDate.setDate(1);
            } else {
              closingDate.setDate(date+1);
            }
          } else {
            if(date === 28) {
              closingDate.setMonth(month+1);
              closingDate.setDate(1);
            } else {
              closingDate.setDate(date+1);
            }

          }
          closingDate.setHours(hour, minutes);
          break;
      }
      // console.log("Closing Info: " + closingDate)
    }
    return closingDate.getTime();
  }

  isParkOpen() {
    return (this.currentTime > this.openingTime && this.currentTime < this.closingTime);
  }

  onContentScroll(scrollTop){
    this.showHeaderBottomLine = ( scrollTop > 0 );
  }
  openRestroomGroups($event){
    if (this.form.dirty) {
      let drawer = this.kngDrawer.launch(RestroomAlertComponent, {
        windowClass: "alert-modal-content"
      });
      drawer.result
        .then(res => {
          console.log(res);
          if (res == false) {
            this.router.navigateByUrl('areas/' + this.area.areaId + '/restroom-groups');
          } else if(res == true) {
            this.submitForm(this.form.value);
            this.router.navigateByUrl('areas/' + this.area.areaId + '/restroom-groups');
          }
        });
    } else {
      this.router.navigateByUrl('areas/' + this.area.areaId + '/restroom-groups');
    }
    //this.router.navigateByUrl('areas/' + this.area.areaId + '/restroom-groups');
    // if(this.form.dirty){
    //   let drawer = this.kngDrawer.launch(RestroomAlertComponent,{
    //     windowClass: "alert-modal-content"
    //   });
      
    //   drawer.result
    //     .then(res=>{
    //       console.log(res);
    //       if(!res) {
    //         // discard and continue
    //         // resu == false
    //         console.log("continue to manage restroom panel")
    //         this.close();
    //       }
    //       else {
    //         //res == true
    //         this.submitForm(this.form.value)
    //         //launch restroom grouping module
    //       }
    //     })
    //     .catch(err=>{
    //       console.log("cancel");
    //     })
    // } else {
    //   //this.close()
    //   this.openRestroomGroupsPanel();
    //  // this.close();

    //  // this.openRestroomGroupEvent.fire('restroomGroup.edit', this.area);
    //   // launch restroom grouping module
    // }

  }

  openImageSelect() {
    let drawer = this.kngDrawer.launch(AreasImageSelector,{
      windowClass: "kng-drawer-threshold"
    });
    drawer.componentInstance.selectedImage = this.area.imageSrc;

    drawer.result
      .then(res=>{
        this.imageSrc = res;
        this.area.imageSrc = res;
      })
      .catch(err=>{
        console.log("cancel");
      })
  }
}



// WEBPACK FOOTER //
// D:/dclean-web-latest/task/cot-web/src/app/components/areas/form/form.component.ts